import axios from "axios";
import moment from "moment";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as Yup from "yup";

const BASE_URL = "api/events";

const initFilters = {
  ageCategory: "0",
  stream: "0",
  keyword: "",
  eventStatus: "0",
};

const FILTER_SCHEMA = Yup.object().shape({
  stream: Yup.number().required(),
  ageCategory: Yup.number().required(),
  keyword: Yup.string(),
  eventStatus: Yup.string(),
});


const sortOptions = [
  { value: 1, label: "Upcoming first" },
  { value: 2, label: "Future events first" },
  { value: 3, label: "Name: A to Z" },
  { value: 4, label: "Name: Z to A" },
];

const STREAM = [
  { value: 0, label: "All" },
  { value: 1, label: "Programming" },
  { value: 2, label: "Robotics" },
  { value: 3, label: "Innovation" },
];

const AGE_CATEGORY = [
  { value: 0, label: "All" },
  { value: 1, label: "Nebula (6 - 9 yrs)" },
  { value: 2, label: "Star (9 - 13 yrs)" },
  { value: 3, label: "Super Nova (12 - 15 yrs)" },
  { value: 4, label: "Galaxy (15 - 20 yrs)" },
];

const EVENT_STATUS = [
  { value: "0", label: "All" },
  { value: "1", label: "Upcoming" },
  { value: "2", label: "Past" },
];
const EventsContext = createContext();

export function useEventsContext() {
  return useContext(EventsContext);
}

export const EventsConsumer = EventsContext.Consumer;

export function EventsContextProvider({ children }) {
  let [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [refetch, setRefetch] = useState(false);
  const [eventIndex, setEventIndex] = useState(0);
  const [filters, setFilters] = useState(initFilters);
  const [eventsPerPage, setEventsPerPage] = useState(5);
  const [displayEvents, setDisplayEvents] = useState([]);
  const [eventPageNumber, setEventPageNumber] = useState(1);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [sortOptionSelected, setSortOptionSelected] = useState(1);

  const handleCloseFilterModal = () => {
    setShowFilterModal(false);
    setEventIndex(0);
    setEventsPerPage(5);
    setEventPageNumber(1);
  };

  const handleShowFilterModal = () => {
    setShowFilterModal(true);
    setEventIndex(0);
    setEventsPerPage(5);
    setEventPageNumber(1);
  };

  const fetchEvents = () => {
    axios
      .get(BASE_URL)
      .then((results) => {
        if (results && results.status === 200) {
          setEvents(results.data);
        }
      })
      .catch(console.log);
  };

  const sortEvents = (eventsToSort) => {
    let eventsToDisplay = [...eventsToSort];
    eventsToDisplay.sort((a, b) => {
      switch (sortOptionSelected + "") {

        case "1":
          return moment(a.startDate, "YYYY-MM-DD").isBefore(
            moment(b.startDate, "YYYY-MM-DD")
          )
            ? -1
            : moment(a.startDate, "YYYY-MM-DD").isAfter(
                moment(b.startDate, "YYYY-MM-DD")
              )
            ? 1
            : 0;
        case "2":
          return moment(a.startDate, "YYYY-MM-DD").isAfter(
            moment(b.startDate, "YYYY-MM-DD")
          )
            ? -1
            : moment(a.startDate, "YYYY-MM-DD").isBefore(
                moment(b.startDate, "YYYY-MM-DD")
              )
            ? 1
            : 0;
        case "3":
          return a.eventName < b.eventName
            ? -1
            : a.eventName > b.eventName
            ? 1
            : 0;
        case "4":
          return a.eventName > b.eventName
            ? -1
            : a.eventName < b.eventName
            ? 1
            : 0;
        default:
          return a.eventId - b.eventId;
      }
    });
    setDisplayEvents(eventsToDisplay);
    setEventIndex(0);
    setEventsPerPage(5);
    setEventPageNumber(1);
  };

  useEffect(() => {
    const setParamFilters = () => {
      let streamParam = searchParams.get("streamId") ?? "0";
      let ageParam = searchParams.get("ageId") ?? "0";
      let eventStatusParam = searchParams.get("eventStatus") ?? "0";
      let nfilters = {
        ageCategory: ageParam,
        stream: streamParam,
        keyword: "",
        eventStatus: eventStatusParam,
      };
      setFilters(nfilters);
    };

    setParamFilters();
  }, [searchParams]);

  useEffect(() => {
    fetchEvents();
    setEventIndex(0);
    setEventsPerPage(5);
    setEventPageNumber(1);
  }, [refetch]);

  useEffect(() => {
    const initFiltering = () => {
      setDisplayEvents(events);
      sortEvents(events);
      setEventIndex(0);
      setEventsPerPage(5);
      setEventPageNumber(1);
    };
    initFiltering();
  }, [events]);

  useEffect(() => {
    const filterEvents = () => {
     // console.log("Filtering", filters);
      let eventsToDisplay = events.filter((e) => {
        if (filters.keyword !== "" && !e.eventName.toLowerCase().includes(filters.keyword.toLowerCase())) {
          return false;
        }
        if (filters.ageCategory !== "0" && filters.stream !== "0") {
          return (
            e.streams.filter((s) => {
              return (
                s.streamId.toString() === filters.stream &&
                s.age_categories.filter((ac) => {
                  return ac.ageCategoryId.toString() === filters.ageCategory;
                }).length > 0
              );
            }).length > 0
          );
        } else {
          let ageCategories = [];
          let streams = [];
          e.streams.forEach((s) => {
            streams.push(s.streamId.toString());
            s.age_categories.forEach((ac) => {
              ageCategories.push(ac.ageCategoryId.toString());
            });
          });
          let ageCategoryInclusion =
            filters.ageCategory === "0" ||
            ageCategories.includes(filters.ageCategory);
          let streamInclusion =
            filters.stream === "0" || streams.includes(filters.stream);

          return ageCategoryInclusion && streamInclusion;
        }
      });
      if (filters.eventStatus !== "0") {
        const currentDate = moment().startOf("day");
        eventsToDisplay = eventsToDisplay.filter((e) => {
          const eventStartDate = moment(e.startDate, "YYYY-MM-DD").startOf(
            "day"
          );
          const isUpcoming = eventStartDate.isSameOrAfter(currentDate);
          const isPast = eventStartDate.isBefore(currentDate);

          if (filters.eventStatus === "1" && isUpcoming) {
            return true;
          } else if (filters.eventStatus === "2" && isPast) {
            return true;
          }
          return false;
        });
      }
      setDisplayEvents(eventsToDisplay);
      setEventIndex(0);
      setEventsPerPage(5);
      setEventPageNumber(1);
    };
    filterEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    sortEvents(displayEvents);
  }, [sortOptionSelected]);

  const EventContext = {
    events,
    setEvents,
    displayEvents,
    setDisplayEvents,
    filters,
    setFilters,
    refetch,
    setRefetch,
    showFilterModal,
    setShowFilterModal,
    handleCloseFilterModal,
    handleShowFilterModal,
    sortOptions,
    eventIndex,
    setEventIndex,
    eventsPerPage,
    setEventsPerPage,
    eventPageNumber,
    setEventPageNumber,
    sortOptionSelected,
    setSortOptionSelected,
    FILTER_SCHEMA,
    FILTER_TYPES: {
      STREAM,
      AGE_CATEGORY,
      EVENT_STATUS,
    },
  };

  return (
    <EventsContext.Provider value={EventContext}>
      {children}
    </EventsContext.Provider>
  );
}
